/**
 * LookupsSettings — Admin Master Settings for 共通マスター
 *
 * The 3 shared lookup masters (メーカー / 単位 / 材質) get populated organically
 * as users type new values in 資材 / 部品受注入力 / 品目マスター dropdowns
 * (via <LookupCombobox />). Over time this bloats + accumulates typo
 * duplicates ("KYOCERA" vs "Kyocera" vs "京セラ").
 *
 * This admin page gives 4 tools per master:
 *   1. 表示名変更 (rename) — also rewrites the name in every downstream row
 *      (mfg_items / shizai_requests.lines / shizai_orders.lines) so
 *      historical rows adopt the new canonical label.
 *   2. 並び順 (sort_order) — smaller number sinks to top of dropdown.
 *   3. 表示/非表示 (soft-hide active=False) — hides from dropdown but keeps
 *      historical rows readable.
 *   4. 統合 (merge duplicates → canonical) — pick 1 canonical + N duplicates,
 *      backend rewrites every downstream `name` string then soft-hides the
 *      duplicates.
 *
 * The list endpoint includes `include_hidden=true` so admins can un-hide
 * previously-removed rows.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Ruler, Cube, PencilSimple, EyeSlash, Eye,
  ArrowsMerge, Info, MagnifyingGlass, Warning, Check,
  SortAscending, ListNumbers,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/lookups`;

const KINDS = [
  { key: 'makers',    label: 'メーカー', Icon: Package,  accent: 'from-blue-500 to-indigo-600'   },
  { key: 'uoms',      label: '単位',     Icon: Ruler,    accent: 'from-emerald-500 to-green-600' },
  { key: 'materials', label: '材質',     Icon: Cube,     accent: 'from-orange-500 to-rose-600'   },
];

export default function LookupsSettings() {
  const [tab, setTab] = useState('makers');
  const [rowsByKind, setRowsByKind] = useState({ makers: [], uoms: [], materials: [] });
  const [sortModeByKind, setSortModeByKind] = useState({ makers: 'aiueo', uoms: 'aiueo', materials: 'aiueo' });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editRow, setEditRow] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [sortSaving, setSortSaving] = useState(false);

  const load = useCallback(async (kind) => {
    setLoading(true);
    try {
      const [rowsRes, prefRes] = await Promise.all([
        axios.get(`${API}/${kind}`, { params: { include_hidden: true } }),
        axios.get(`${API}/${kind}/pref`).catch(() => ({ data: { sort_mode: 'aiueo' } })),
      ]);
      setRowsByKind((prev) => ({ ...prev, [kind]: rowsRes.data || [] }));
      setSortModeByKind((prev) => ({ ...prev, [kind]: prefRes.data?.sort_mode || 'aiueo' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'マスターの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // Pre-load all 3 kinds once on mount so tab count chips are accurate before
  // the user visits each tab.
  useEffect(() => {
    KINDS.forEach(({ key }) => {
      axios.get(`${API}/${key}`, { params: { include_hidden: true } })
        .then((r) => setRowsByKind((prev) => ({ ...prev, [key]: r.data || [] })))
        .catch(() => { /* per-tab load below will surface the error */ });
    });
  }, []);

  useEffect(() => { load(tab); setSelected(new Set()); setQuery(''); }, [tab, load]);

  const rows = useMemo(() => rowsByKind[tab] || [], [rowsByKind, tab]);
  const sortMode = sortModeByKind[tab] || 'aiueo';
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.code || '').toLowerCase().includes(q) ||
      (r.yomi || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const activeKind = KINDS.find((k) => k.key === tab);
  const showCode = tab === 'makers';

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const changeSortMode = async (mode) => {
    if (mode === sortMode || sortSaving) return;
    setSortSaving(true);
    try {
      await axios.put(`${API}/${tab}/pref`, { sort_mode: mode });
      setSortModeByKind((prev) => ({ ...prev, [tab]: mode }));
      toast.success(mode === 'aiueo' ? '五十音順に切り替えました' : '並び順(手動)に切り替えました');
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '並び順の変更に失敗しました');
    } finally {
      setSortSaving(false);
    }
  };

  const doHide = async (row) => {
    if (!window.confirm(`「${row.name}」を非表示にしますか？（後で再表示可）`)) return;
    try {
      await axios.delete(`${API}/${tab}/${row.id}`);
      toast.success('非表示にしました');
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '非表示に失敗しました');
    }
  };

  const doUnhide = async (row) => {
    try {
      await axios.put(`${API}/${tab}/${row.id}`, { active: true });
      toast.success('再表示しました');
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '再表示に失敗しました');
    }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="settings-lookups">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <ArrowsMerge size={26} weight="duotone" className="text-indigo-500" />
            共通マスター管理
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            <span className="font-medium">メーカー / 単位 / 材質</span> の共通マスターです。
            資材・部品受注・品目マスターの入力候補として全アプリで共有されます。
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="lookups-tabs">
        {KINDS.map(({ key, label, Icon, accent }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`lookups-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tab === key
                ? `bg-gradient-to-r ${accent} text-white shadow-md`
                : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}
          >
            <Icon size={16} weight={tab === key ? 'fill' : 'duotone'} />
            <span>{label}</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              tab === key ? 'bg-white/25 text-white' : 'bg-zinc-100 text-zinc-500'
            }`}>{(rowsByKind[key] || []).length}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={showCode
              ? `${activeKind?.label}を検索 (名称・コード・読み)...`
              : `${activeKind?.label}を検索 (名称・読み)...`}
            className="h-9 pl-8 text-sm"
            data-testid="lookups-search"
          />
        </div>
        {/* Sort mode toggle */}
        <div
          className="inline-flex items-center rounded-lg border border-zinc-200 bg-white overflow-hidden shrink-0"
          data-testid="lookups-sort-mode"
        >
          <button
            type="button"
            onClick={() => changeSortMode('aiueo')}
            disabled={sortSaving}
            className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold transition-colors ${
              sortMode === 'aiueo'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-zinc-600 hover:bg-zinc-50'
            }`}
            title="名称の読み仮名(ひらがな)順で並べます"
            data-testid="lookups-sort-mode-aiueo"
          >
            <SortAscending size={13} weight="bold" />
            五十音順
          </button>
          <button
            type="button"
            onClick={() => changeSortMode('manual')}
            disabled={sortSaving}
            className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border-l border-zinc-200 transition-colors ${
              sortMode === 'manual'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-zinc-600 hover:bg-zinc-50'
            }`}
            title="編集画面の「並び順」の数値順で並べます"
            data-testid="lookups-sort-mode-manual"
          >
            <ListNumbers size={13} weight="bold" />
            並び順
          </button>
        </div>
        <Button
          onClick={() => setMergeOpen(true)}
          disabled={selected.size < 2}
          data-testid="lookups-merge-btn"
          className="h-9 bg-teal-700 hover:bg-teal-800 text-white shadow-sm disabled:opacity-40"
        >
          <ArrowsMerge size={14} weight="bold" className="mr-1.5" />
          統合 ({selected.size})
        </Button>
      </div>

      {/* Info banner */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-3 py-2 flex items-start gap-2 text-[12px] text-indigo-900">
        <Info size={16} weight="duotone" className="text-indigo-500 shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">統合機能:</span> 複数チェック → 「統合」ボタン → 正となる項目を選択すると、過去の資材/発注データ内の名称も自動書き換えされます。
          {tab === 'makers' && (
            <span className="block mt-0.5 text-indigo-700">
              <span className="font-semibold">🔗 品目マスター由来:</span> 「mfg_items由来」タグ付きの行は品目マスター側で登録された値です（このページで直接編集不可）。
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-xs font-semibold text-zinc-600">
                <th className="px-3 py-2.5 w-10"></th>
                <th className="px-3 py-2.5 w-20">並び順</th>
                {showCode && <th className="px-3 py-2.5 w-28">コード</th>}
                <th className="px-3 py-2.5">名称</th>
                <th className="px-3 py-2.5 w-32">状態</th>
                <th className="px-3 py-2.5 w-40">操作</th>
              </tr>
            </thead>
            <tbody data-testid="lookups-list">
              {loading && filtered.length === 0 ? (
                <tr><td colSpan={showCode ? 6 : 5} className="px-4 py-10 text-center text-sm text-zinc-400">読込中...</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={showCode ? 6 : 5} className="px-4 py-12 text-center" data-testid="lookups-empty">
                    <div className="flex flex-col items-center gap-2">
                      <activeKind.Icon size={40} weight="duotone" className="text-zinc-300" />
                      <div className="text-sm text-zinc-500">
                        {query ? '検索結果がありません' : `${activeKind?.label}が登録されていません`}
                      </div>
                      <p className="text-xs text-zinc-400 max-w-md leading-relaxed">
                        資材・品目マスター・部品受注入力の各フォームで「＋ 新規追加」から登録されると、自動的にここに反映されます。
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((r) => {
                const isItemSource = r.source === 'mfg_items';
                const isHidden = r.active === false;
                const isSelected = selected.has(r.id);
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-zinc-200 transition-colors ${
                      isSelected ? 'bg-indigo-50/70' : (isHidden ? 'bg-zinc-50/70 text-zinc-500' : 'hover:bg-zinc-50/50')
                    }`}
                    data-testid={`lookups-row-${r.id}`}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(r.id)}
                        className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                        data-testid={`lookups-select-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-zinc-500">
                      {r.sort_order != null ? String(r.sort_order).padStart(4, '0') : <span className="text-zinc-300">—</span>}
                    </td>
                    {showCode && (
                      <td className="px-3 py-2.5 tabular-nums text-zinc-700 font-mono text-[12px]" data-testid={`lookups-code-${r.id}`}>
                        {r.code ? r.code : <span className="text-zinc-300">—</span>}
                      </td>
                    )}
                    <td className="px-3 py-2.5 font-medium">
                      <div className="flex flex-col">
                        <span className={isHidden ? 'line-through' : ''}>{r.name}</span>
                        {r.yomi && (
                          <span className="text-[10px] text-zinc-400 font-normal">{r.yomi}</span>
                        )}
                      </div>
                      {isItemSource && (
                        <span className="ml-0 mt-1 inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold">
                          mfg_items由来
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isHidden
                        ? <span className="px-2 py-0.5 rounded bg-zinc-200 text-zinc-600 text-[11px] font-medium">非表示</span>
                        : <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-medium">表示中</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline" size="sm"
                          className="h-7 px-2 text-xs disabled:opacity-40"
                          onClick={() => setEditRow(r)}
                          disabled={isItemSource}
                          data-testid={`lookups-edit-${r.id}`}
                          title={isItemSource ? '品目マスターで編集してください' : '編集'}
                        >
                          <PencilSimple size={12} className="mr-1" /> 編集
                        </Button>
                        {isHidden ? (
                          <Button
                            variant="outline" size="sm"
                            className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-40"
                            onClick={() => doUnhide(r)}
                            disabled={isItemSource}
                            data-testid={`lookups-unhide-${r.id}`}
                          >
                            <Eye size={12} className="mr-1" /> 再表示
                          </Button>
                        ) : (
                          <Button
                            variant="outline" size="sm"
                            className="h-7 px-2 text-xs text-rose-600 border-rose-200 hover:bg-rose-50 disabled:opacity-40"
                            onClick={() => doHide(r)}
                            disabled={isItemSource}
                            data-testid={`lookups-hide-${r.id}`}
                          >
                            <EyeSlash size={12} className="mr-1" /> 非表示
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editRow && (
        <EditDialog
          kind={tab}
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(tab); }}
        />
      )}

      {mergeOpen && (
        <MergeDialog
          kind={tab}
          rows={rows.filter((r) => selected.has(r.id))}
          onClose={() => setMergeOpen(false)}
          onMerged={() => { setMergeOpen(false); setSelected(new Set()); load(tab); }}
        />
      )}
    </div>
  );
}


function EditDialog({ kind, row, onClose, onSaved }) {
  const [name, setName] = useState(row.name || '');
  const [sortOrder, setSortOrder] = useState(row.sort_order ?? '');
  const [code, setCode] = useState(row.code || '');
  const [yomi, setYomi] = useState(row.yomi || '');
  const [saving, setSaving] = useState(false);
  const showCode = kind === 'makers';

  const save = async () => {
    if (!name.trim()) { toast.error('名称は必須です'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim() };
      if (String(sortOrder).trim() !== '') body.sort_order = Number(sortOrder);
      else body.sort_order = null;
      if (showCode) body.code = code.trim();
      body.yomi = yomi.trim(); // empty string → backend auto-regenerates
      await axios.put(`${API}/${kind}/${row.id}`, body);
      toast.success('保存しました');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="lookups-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilSimple size={20} weight="duotone" className="text-indigo-500" />
            編集
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-600">名称 ＊</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="h-10 mt-1" data-testid="lookups-edit-name" />
            <p className="text-[11px] text-zinc-400 mt-1">
              変更した場合、過去の資材・発注・品目マスターの該当値も同時に書き換えられます。
            </p>
          </div>
          {showCode && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">コード</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)}
                     placeholder="例: KYO001"
                     className="h-10 mt-1 font-mono text-sm" data-testid="lookups-edit-code" />
              <p className="text-[11px] text-zinc-400 mt-1">
                旧システム互換のメーカーコード。空欄可。入力候補ボックスで「コード」で検索可能になります。
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">読み (ひらがな)</Label>
            <Input value={yomi} onChange={(e) => setYomi(e.target.value)}
                   placeholder={row.yomi || '自動生成されます'}
                   className="h-10 mt-1" data-testid="lookups-edit-yomi" />
            <p className="text-[11px] text-zinc-400 mt-1">
              五十音順ソートに使われる読み仮名。空欄にすると名称から自動生成します。
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">並び順</Label>
            <Input type="number" value={sortOrder}
                   onChange={(e) => setSortOrder(e.target.value)}
                   placeholder="空欄でリスト末尾"
                   className="h-10 mt-1 tabular-nums" data-testid="lookups-edit-sort" />
            <p className="text-[11px] text-zinc-400 mt-1">
              「並び順」モードで有効。「五十音順」モードでも値が入っている行は上部固定になります。
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="lookups-edit-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MergeDialog({ kind, rows, onClose, onMerged }) {
  // The row the admin picks as the canonical one to keep. All others get
  // rewritten and hidden.
  const [canonicalId, setCanonicalId] = useState(rows[0]?.id || '');
  const [saving, setSaving] = useState(false);

  const canonical = rows.find((r) => r.id === canonicalId);
  const duplicates = rows.filter((r) => r.id !== canonicalId);

  const doMerge = async () => {
    if (!canonicalId) { toast.error('正となる項目を選択してください'); return; }
    if (duplicates.length === 0) { toast.error('統合対象が2件以上必要です'); return; }
    if (!window.confirm(
      `${duplicates.map((d) => `「${d.name}」`).join(' / ')} を「${canonical.name}」に統合します。\n\n` +
      `※過去の資材・発注データ内の名称も一括で書き換えられます。（後で戻せません）\n\n実行しますか？`
    )) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/${kind}/merge`, {
        canonical_id: canonicalId,
        duplicate_ids: duplicates.map((d) => d.id),
      });
      toast.success(`${r.data.merged.length}件を「${r.data.canonical_name}」に統合しました`);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '統合に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="lookups-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsMerge size={20} weight="duotone" className="text-indigo-500" />
            重複項目を統合
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 flex items-start gap-2 text-[12px] text-amber-900">
            <Warning size={16} weight="duotone" className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              「正」に選ばなかった項目は<span className="font-semibold">非表示</span>になり、
              過去の資材・発注データ内の名称も<span className="font-semibold">「正」の名称に一括書き換え</span>されます。
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600 mb-2 block">
              正となる項目を選択 (残す名称)
            </Label>
            <RadioGroup value={canonicalId} onValueChange={setCanonicalId} data-testid="lookups-merge-canonical-group">
              <div className="space-y-1.5">
                {rows.map((r) => (
                  <label
                    key={r.id}
                    htmlFor={`merge-${r.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                      canonicalId === r.id
                        ? 'border-indigo-400 bg-indigo-50/60'
                        : 'border-zinc-200 hover:bg-zinc-50'
                    }`}
                  >
                    <RadioGroupItem
                      value={r.id}
                      id={`merge-${r.id}`}
                      data-testid={`lookups-merge-canonical-${r.id}`}
                    />
                    <span className="flex-1 text-sm font-medium">{r.name}</span>
                    {canonicalId === r.id && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-bold">
                        <Check size={10} weight="bold" /> 正
                      </span>
                    )}
                    {r.source === 'mfg_items' && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold">
                        mfg由来
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button
            onClick={doMerge}
            disabled={saving || !canonicalId || duplicates.length === 0}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="lookups-merge-confirm"
          >
            {saving ? '統合中...' : `${duplicates.length}件を統合`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
